import { ScannerProvider, ScannerConfig, ScanResult } from '../types'
import { captureException } from 'lib/monitoring'
import {
  isGS1UDI,
  parseGS1Barcode,
  formatGS1Barcode,
  isDeviceIdOnlyBarcode,
  isMissingEssentialIdentifiers,
  combineBarcodeData,
} from '../../utils/scanner/gs1Utils'

declare global {
  interface Window {
    isScannerFailed?: boolean
    mockScan: (text: string) => void
  }
}

class CortexScanner {
  _isInitialized: boolean
  _isStarting: boolean
  _isStopping: boolean
  _pendingActionHandle?: NodeJS.Timeout
  isScannerActive: boolean

  constructor() {
    this._isInitialized = false
    this._isStarting = false
    this._isStopping = false
    this.isScannerActive = false
  }

  async init() {
    if (!this._isInitialized) {
      console.debug('[CortexScanner]: Initializing')
      await CortexDecoder.CDDecoder.init('/cd')
      await CortexDecoder.CDLicense.activateLicense(
        process.env.REACT_APP_CORTEX_KEY!
      )
      await CortexDecoder.CDCamera.init(document.getElementById('cortex-video'))
      await CortexDecoder.CDPerformanceFeatures.setdataParsing(
        5,
        '000000000000|;^1^C'
      )
      this._isInitialized = true
      console.debug('[CortexScanner]: Initialized')
    }
  }

  startDecoding() {
    CortexDecoder.CDDecoder.setDecoding(true)
  }

  stopDecoding() {
    CortexDecoder.CDDecoder.setDecoding(false)
  }

  async start({
    onResult,
  }: {
    onResult: (result: { results: any[] }) => void
  }) {
    if (!this._isInitialized) {
      throw new Error('CortexScanner is not initialized')
    }

    const startFn = async () => {
      console.debug('[CortexScanner]: Starting scanner')
      this._isStarting = true
      await waitForIdle()
      await CortexDecoder.CDCamera.init(document.getElementById('cortex-video'))
      await CortexDecoder.CDCamera.startCamera()
      await CortexDecoder.CDCamera.startPreview(onResult)
      CortexDecoder.CDDecoder.setDecoding(true)
      CortexDecoder.CDCamera.videoCapturing = true
      this._isStarting = false
      this.isScannerActive = true
      console.debug('[CortexScanner]: Scanner started')
    }

    if (this._isStopping || this._isStarting) {
      this._clearPendingAction()

      console.debug('[CortexScanner]: Scheduling scanner start')
      this._pendingActionHandle = setInterval(() => {
        this._clearPendingAction()
        if (this.isScannerActive) {
          this.stop().then(startFn)
        } else {
          startFn()
          console.debug('[CortexScanner]: Executing scheduled start')
        }
      }, 250)
    } else {
      await startFn()
    }
  }

  async stop() {
    if (!this._isInitialized) {
      throw new Error('CortexScanner is not initialized')
    }

    if (!this._isStopping && this.isScannerActive) {
      this._clearPendingAction()

      const stopFn = async () => {
        console.debug('[CortexScanner]: Stopping scanner')
        this._isStopping = true
        await CortexDecoder.CDCamera.stopCamera()
        await CortexDecoder.CDCamera.stopPreview()
        CortexDecoder.CDDecoder.setDecoding(false)
        CortexDecoder.CDCamera.videoCapturing = false
        this._isStopping = false
        this.isScannerActive = false
        console.debug('[CortexScanner]: Scanner stopped')
      }

      if (this._isStarting) {
        console.debug('[CortexScanner]: Scheduling scanner stop')
        this._pendingActionHandle = setInterval(() => {
          if (!this._isStarting) {
            this._clearPendingAction()
            stopFn()
            console.debug('[CortexScanner]: Executing scheduled stop')
          }
        }, 250)
      } else {
        await stopFn()
      }
    }
  }

  async setROI(cdRect: any) {
    await CortexDecoder.CDDecoder.setRegionOfInterest([cdRect])
  }

  async drawROI(cdRect: any) {
    let canvas = document.querySelector('canvas')
    canvas = canvas ?? document.createElement('canvas')
    const videoElement = document.getElementById('cortex-video')
    const videoContainer = document.getElementById('video-container')
    const ctx = canvas.getContext('2d')

    if (videoElement && videoContainer && ctx) {
      canvas.id = 'ROIcanvas'
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
      canvas.style.zIndex = '5'
      canvas.style.position = 'absolute'
      canvas.style.marginLeft = 'auto'
      canvas.style.marginRight = 'auto'
      canvas.style.left = '0'
      canvas.style.right = '0'
      canvas.style.textAlign = 'center'

      const { TopLeft, TopRight, BottomLeft, BottomRight } = cdRect

      const x = Math.min(
        parseInt(TopLeft.X, 10),
        parseInt(TopRight.X, 10),
        parseInt(BottomLeft.X, 10),
        parseInt(BottomRight.X, 10)
      )
      const y = Math.min(
        parseInt(TopLeft.Y, 10),
        parseInt(TopRight.Y, 10),
        parseInt(BottomLeft.Y, 10),
        parseInt(BottomRight.Y, 10)
      )
      const width =
        Math.max(
          parseInt(TopLeft.X, 10),
          parseInt(TopRight.X, 10),
          parseInt(BottomLeft.X, 10),
          parseInt(BottomRight.X, 10)
        ) - x
      const height =
        Math.max(
          parseInt(TopLeft.Y, 10),
          parseInt(TopRight.Y, 10),
          parseInt(BottomLeft.Y, 10),
          parseInt(BottomRight.Y, 10)
        ) - y

      // Clear the entire canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // draw semi-transparent black overlay
      ctx.fillStyle = 'rgba(0, 0, 0, 0.25)'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.clearRect(x, y, width, height)

      // draw box
      ctx.strokeStyle = 'white'
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.moveTo(parseInt(TopLeft.X, 10), parseInt(TopLeft.Y, 10))
      ctx.lineTo(parseInt(TopRight.X, 10), parseInt(TopRight.Y, 10))
      ctx.lineTo(parseInt(BottomRight.X, 10), parseInt(BottomRight.Y, 10))
      ctx.lineTo(parseInt(BottomLeft.X, 10), parseInt(BottomLeft.Y, 10))
      ctx.closePath()
      ctx.stroke()

      videoContainer.appendChild(canvas)
    }
  }

  _clearPendingAction() {
    clearInterval(this._pendingActionHandle)
    this._pendingActionHandle = undefined
  }
}

function waitForIdle() {
  return new Promise((resolve) => {
    if (window.requestIdleCallback) {
      window.requestIdleCallback(resolve)
    } else {
      setTimeout(resolve, 0)
    }
  })
}

export class CortexScannerProvider implements ScannerProvider {
  private scanner: CortexScanner
  private nonGS1Timer: NodeJS.Timeout | null = null
  private lastNonGS1Barcode: {
    barcodeData: string
    symbologyName: string
  } | null = null
  private currentConfig: ScannerConfig | null = null

  // State for handling split barcodes
  private splitBarcodeTimer: NodeJS.Timeout | null = null
  private collectedBarcodes: Array<{
    barcodeData: string
    symbologyName: string
    timestamp: number
  }> = []
  private isCollectingMultipleBarcodes: boolean = false
  private readonly SPLIT_BARCODE_TIMEOUT = 5000 // 5 seconds

  constructor() {
    this.scanner = new CortexScanner()
  }

  async init(): Promise<void> {
    try {
      await this.scanner.init()
    } catch (error) {
      captureException(error as Error)
      throw error
    }
  }

  async start(config: ScannerConfig): Promise<void> {
    this.currentConfig = config

    const onResult = ({ results }: { results: any[] }) => {
      if (results.length === 0) return

      // Process each result
      for (const result of results) {
        this.processBarcodeResult(result, config)
      }
    }

    await this.scanner.start({ onResult })
  }

  /**
   * Processes a barcode result and handles split barcode logic
   */
  private processBarcodeResult(result: any, config: ScannerConfig): void {
    const barcodeData = result.barcodeData || result.text || result.data
    const symbologyName = result.symbologyName || result.format || 'unknown'

    // Check if this is a GS1 barcode
    let gs1Barcode = null
    let nonGS1Barcode = null

    if (isGS1UDI(barcodeData)) {
      gs1Barcode = { barcodeData, symbologyName }
    } else {
      nonGS1Barcode = { barcodeData, symbologyName }
    }

    // Handle split barcode collection mode
    if (this.isCollectingMultipleBarcodes) {
      if (gs1Barcode) {
        // Add this barcode to our collection if it's not a duplicate
        this.addToCollection(gs1Barcode)
      }
      // Don't process individual barcodes while collecting - wait for timer
      return
    }

    // Check if we found a device ID-only barcode to start collection mode
    if (gs1Barcode && isDeviceIdOnlyBarcode(gs1Barcode.barcodeData)) {
      // Clear any existing non-GS1 timer since we're starting split barcode collection
      if (this.nonGS1Timer) {
        clearTimeout(this.nonGS1Timer)
        this.nonGS1Timer = null
      }
      this.lastNonGS1Barcode = null

      // Start collecting multiple barcodes
      this.startSplitBarcodeCollection(gs1Barcode, config)
      return
    }

    // Check if we found a potentially split barcode (missing device ID but has other identifiers)
    if (gs1Barcode && isMissingEssentialIdentifiers(gs1Barcode.barcodeData)) {
      // Clear any existing non-GS1 timer
      if (this.nonGS1Timer) {
        clearTimeout(this.nonGS1Timer)
        this.nonGS1Timer = null
      }
      this.lastNonGS1Barcode = null

      // Start collecting multiple barcodes with this non-device-ID barcode
      this.startSplitBarcodeCollection(gs1Barcode, config)
      return
    }

    // If we found a complete GS1 barcode (not device ID-only and has essential identifiers), process normally
    if (gs1Barcode) {
      if (this.nonGS1Timer) {
        clearTimeout(this.nonGS1Timer)
        this.nonGS1Timer = null
      }
      this.lastNonGS1Barcode = null

      // Parse and format the GS1 barcode using shared utilities
      const identifiers = parseGS1Barcode(gs1Barcode.barcodeData)
      const formattedBarcode = formatGS1Barcode(identifiers)

      const scanResult: ScanResult = {
        barcodeData: formattedBarcode || gs1Barcode.barcodeData, // Fallback to original if parsing fails
        symbologyName: gs1Barcode.symbologyName,
      }

      config.onScan(scanResult)
      return
    }

    // If we found a non-GS1 barcode and don't have a pending timer
    if (nonGS1Barcode && !this.nonGS1Timer) {
      this.lastNonGS1Barcode = nonGS1Barcode

      // Set a 3-second timer to process the non-GS1 barcode if no GS1 is found
      this.nonGS1Timer = setTimeout(() => {
        if (this.lastNonGS1Barcode) {
          const scanResult: ScanResult = {
            barcodeData: this.lastNonGS1Barcode.barcodeData,
            symbologyName: this.lastNonGS1Barcode.symbologyName,
          }
          config.onScan(scanResult)
          this.lastNonGS1Barcode = null
        }
        this.nonGS1Timer = null
      }, 3000)
    }
  }

  async stop(): Promise<void> {
    // Clean up timers and state
    if (this.nonGS1Timer) {
      clearTimeout(this.nonGS1Timer)
      this.nonGS1Timer = null
    }
    this.lastNonGS1Barcode = null

    // Clean up split barcode collection state
    this.resetSplitBarcodeState()

    await this.scanner.stop()
  }

  async setROI(rect: any): Promise<void> {
    await this.scanner.setROI(rect)
  }

  async drawROI(rect: any): Promise<void> {
    await this.scanner.drawROI(rect)
  }

  startDecoding(): void {
    this.scanner.startDecoding()
  }

  stopDecoding(): void {
    // Clean up split barcode collection state
    this.resetSplitBarcodeState()

    this.scanner.stopDecoding()
  }

  isScannerActive(): boolean {
    return this.scanner.isScannerActive
  }

  /**
   * Combines multiple barcode texts into a single GS1 formatted string
   * Uses shared utility function
   */
  private combineBarcodes(
    barcodes: Array<{ barcodeData: string; symbologyName: string }>
  ): string {
    const barcodeDataArray = barcodes.map((b) => b.barcodeData)
    return combineBarcodeData(barcodeDataArray)
  }

  /**
   * Processes a collected set of barcodes and triggers the onScan callback
   */
  private processCollectedBarcodes(config: ScannerConfig): void {
    if (this.collectedBarcodes.length === 0) return

    const combinedBarcodeData = this.combineBarcodes(this.collectedBarcodes)

    // Use the format from the first barcode (they should all be similar formats)
    const symbologyName = this.collectedBarcodes[0].symbologyName

    const scanResult: ScanResult = {
      barcodeData: combinedBarcodeData,
      symbologyName,
    }

    config.onScan(scanResult)
    this.resetSplitBarcodeState()
  }

  /**
   * Starts the collection mode for split barcodes
   */
  private startSplitBarcodeCollection(
    initialBarcode: { barcodeData: string; symbologyName: string },
    config: ScannerConfig
  ): void {
    this.isCollectingMultipleBarcodes = true
    this.collectedBarcodes = [
      {
        barcodeData: initialBarcode.barcodeData,
        symbologyName: initialBarcode.symbologyName,
        timestamp: Date.now(),
      },
    ]

    // Set a 5-second timer to process collected barcodes
    this.splitBarcodeTimer = setTimeout(() => {
      this.processCollectedBarcodes(config)
    }, this.SPLIT_BARCODE_TIMEOUT)
  }

  /**
   * Adds a barcode to the collection if we're in collection mode
   */
  private addToCollection(barcode: {
    barcodeData: string
    symbologyName: string
  }): boolean {
    if (!this.isCollectingMultipleBarcodes) return false

    // Check if this barcode is already in our collection
    const isDuplicate = this.collectedBarcodes.some(
      (collected) => collected.barcodeData === barcode.barcodeData
    )

    if (!isDuplicate) {
      this.collectedBarcodes.push({
        barcodeData: barcode.barcodeData,
        symbologyName: barcode.symbologyName,
        timestamp: Date.now(),
      })
    }

    return true
  }

  /**
   * Resets the split barcode collection state
   */
  private resetSplitBarcodeState(): void {
    if (this.splitBarcodeTimer) {
      clearTimeout(this.splitBarcodeTimer)
      this.splitBarcodeTimer = null
    }
    this.collectedBarcodes = []
    this.isCollectingMultipleBarcodes = false
  }
}
